<template>
    <div>
        <div class="level is-vcentered">
            <div class="level-left">
                <h1 class="title is-size-4">Manage Projects & Ownership</h1>
            </div>
            <div class="level-right">
                <b-button
                    class="level-item button is-primary is-outlined"
                    @click="exportProjectList"
                    icon-left="file-csv"
                    :disabled="isExporting"
                    >
                    Download Project List
                    <i v-if="isExporting" class="fas fa-spinner fa-spin is-small"></i>
                </b-button>
                <router-link class="level-item button is-outlined" to="/Admin">Back to Admin</router-link>
            </div>
        </div>
        <b-notification type="is-danger" v-if="errorMessage" :closable="false">
            {{ errorMessage }}
        </b-notification>
        <!-- Search input -->
        <b-field>
            <b-input
                rounded
                v-model="searchName"
                :data="filteredProjectArray"
                placeholder="search..."
                icon="search"
            >
            </b-input>
        </b-field>
        <!-- project Table -->
        <b-table
            class="box"
            :data="searchName ? filteredProjectArray : projects"
            :paginated="true"
            :per-page="itemsPerPage"
            :current-page.sync="currentPage"
            :pagination-simple="false"
            :pagination-position="'both'"
            :default-sort-direction="'asc'"
            :striped="true"
            :loading="isLoading"
            default-sort="id"
        >

            <template v-slot:bottom-left>
                {{pageInfo}}
            </template>

            <template slot-scope="props">
                <b-table-column field="id" label="ID" width="100" numeric centered sortable>
                    {{ props.row.id }}
                </b-table-column>

                <b-table-column field="name" label="Project Name" sortable >
                    <span>{{ props.row.name }}</span>
                </b-table-column>

                <b-table-column field="state" label="State" centered sortable>
                    <span class="tag" :class="projectServiceHelper.getStateStyle(props.row.state)">{{ props.row.state }}</span>
                </b-table-column>

                <b-table-column field="owner" label="Owner" sortable >
                    <span>{{ props.row.ownerEmail }}</span>
                    <!-- todo: maybe use owner state for style here? -->
                </b-table-column>

                <b-table-column field="created" label="Created" centered sortable>
                    {{ props.row.created | formatDate }}
                </b-table-column>
                <b-table-column field="lastModified" label="Last Modified" centered sortable>
                    {{ props.row.lastModified | formatDate }}
                </b-table-column>
                <b-table-column field="startDate" label="Start Date" centered sortable>
                    {{ props.row.startDate | formatDate }}
                </b-table-column>
                <b-table-column field="endDate" label="End Date" centered sortable>
                    {{ props.row.endDate | formatDate }}
                </b-table-column>

                <b-table-column label="View Detail" width="100" centered>
                    <div @click.stop>
                        <button class="button is-outlined is-inline-block" @click="$router.push(`/project/view/${props.row.id}`)">
                            <b-icon icon="eye" :type="props.row.isActive ? 'is-primary' : 'has-text-grey'"></b-icon>
                        </button>
                    </div>
                </b-table-column>

                <b-table-column label="Transfer" width="100" centered>
                    <button class="button is-outlined is-inline-block" @click="showTransferProjectForm(props.row)">
                        <b-icon
                            icon="share-square"
                            type="is-primary"
                        >
                        </b-icon>
                    </button>
                </b-table-column>
                <b-table-column label="Archive" width="100" centered>
                    <button v-if="isPublished(props.row.state)" class="button is-outlined is-inline-block" @click="showConfirmArchiveModal(props.row)">
                        <b-icon
                            icon="fas fa-archive"
                            type="is-primary"
                        >
                        </b-icon>
                    </button>
                </b-table-column>
            </template>
            <!-- Empty Table Template -->
            <template slot="empty">
                <empty-table-content/>
            </template>
        </b-table>
    </div>
</template>

<script>
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import _ from 'lodash';

import ProjectTransferForm from '@/components/ProjectTransferForm.vue';
import EmptyTableContent from '@/components/EmptyTableContent.vue';

import projectOwnershipService from '@/services/project-ownership-service.js';
import projectService from '@/services/project-service.js';
import constants from '@/constants.js';

import { projectServiceHelper } from '@/services/project-service-helper.js';

export default {
    name: 'ProjectOwnershipAdmin',
    components: {
        EmptyTableContent
    },
    data () {
        return {
            projectServiceHelper,
            projects: [],
            searchName: '',
            isLoading: true,
            errorMessage: '',
            isExporting: false,
            itemsPerPage: 25,
            currentPage: 1
        };
    },
    async created () {
        try {
            this.projects = (await projectOwnershipService.getAllOwnedProjects()).data;
        } catch (error) {
            this.errorMessage = error.message;
        } finally {
            this.isLoading = false;
        }
    },
    methods: {
        showTransferProjectForm (projectToTransfer) {
            this.$buefy.modal.open({
                parent: this,
                component: ProjectTransferForm,
                hasModalCard: true,
                props: {
                    project: projectToTransfer
                },
                events: {
                    submitEvent: (updatedProject) => this.updateProject(projectToTransfer, updatedProject)
                }
            });
        },
        updateProject (projectToTransfer, updatedProject) {
            Object.assign(projectToTransfer, updatedProject);
            this.$buefy.toast.open({
                message: updatedProject.name + ' has been transferred',
                type: 'is-success'
            });
        },

        showErrorToast (error) {
            let msg;
            if (error.response && error.response.data) {
                msg = error.response.data;
            } else {
                msg = error.message;
            }
            this.$buefy.toast.open({
                message: msg,
                type: 'is-danger'
            });
        },
        async exportProjectList () {
            try {
                this.isExporting = true;
                let nicerProjectList = _.map(this.projects, p => ({
                    'Id': p.id,
                    'Name': p.name,
                    'State': p.state,
                    'Owner': p.ownerEmail,
                    'CreatedBy': p.createdBy,
                    'CreatedDate': p.created,
                    'LastModified': p.lastModified,
                    'StartDate': p.startDate,
                    'EndDate': p.endDate
                }));

                let csvBody = Papa.unparse(nicerProjectList);
                let csvBlob = new Blob([csvBody], { type: 'text/plain;charset=utf-8' });
                saveAs(csvBlob, 'ConversabilityProjectList.csv');
            } catch (error) {
                console.log(error);
            } finally {
                this.isExporting = false;
            }
        },
        showConfirmArchiveModal (projectToArchive) {
            this.$buefy.dialog.confirm({
                title: 'Archive Project',
                message: `Are you sure you want to archive <b>` + projectToArchive.name + `</b>?
                    <hr>
                    <i style="font-size: 0.8em">
                        Archiving a project will immediately halt metric collection.
                        Any metrics that have already been collected will continue to be available.
                        The project will continue to be accessible to the owner and any user that it has been shared with.
                    </i>
                `,
                type: 'is-warning',
                hasIcon: true,
                onConfirm: () => this.archiveProject(projectToArchive)
            });
        },
        archiveProject (projectToArchive) {
            projectService.archiveProject(projectToArchive.id)
                .then(() => {
                    projectToArchive.state = constants.PROJECT_STATE.ARCHIVED;
                    this.$buefy.toast.open({
                        message: projectToArchive.name + ' has been archived',
                        type: 'is-success'
                    });
                })
                .catch(error => this.$buefy.toast.open({
                    message: error.message,
                    type: 'is-danger'
                }));
        },
        isPublished (projectState) {
            return projectState === constants.PROJECT_STATE.PUBLISHED;
        }
    },
    computed: {
        filteredProjectArray () {
            return this.projects.filter(p => {
                return p.name
                    .toLowerCase()
                    .includes(this.searchName.toLowerCase()) ||
                    p.ownerEmail
                        .toLowerCase()
                        .includes(this.searchName.toLowerCase()) ||
                    p.state
                        .toLowerCase()
                        .includes(this.searchName.toLowerCase());
            });
        },
        pageInfo: function () {
            let totalProjects = this.filteredProjectArray.length;
            let startRange = (this.currentPage - 1) * this.itemsPerPage + 1;
            let endRange = Math.min(this.currentPage * this.itemsPerPage, totalProjects);
            return `Showing ${startRange} to ${endRange} of ${totalProjects} Projects`;
        }
    }
};
</script>

<style lang="scss">
.table td {
    vertical-align: middle;
}
</style>
